import type {
  AttributesEntity,
  ValuesEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import { Slider } from '@eliadmin/core/src/components/ui/slider';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';

export function AttributeSlider({
  attribute,
  updateVariantValues,
  className = '',
}: {
  attribute: AttributesEntity;
  updateVariantValues: (attribute: AttributesEntity, value: ValuesEntity) => void;
  className?: string;
}) {
  const setFilter = useCategoryStore(state => state.setFilter);
  const clearTrigger = useCategoryStore(state => state.clearTrigger);
  const filter = useCategoryStore(state => state.filter);

  const findMinValue = (attribute: AttributesEntity) => {
    return attribute.values?.reduce((min, value) => {
      return Math.min(min, parseFloat(value.name ?? Infinity));
    }, Infinity);
  };
  const findMaxValue = (attribute: AttributesEntity) => {
    return attribute.values?.reduce((max, value) => {
      return Math.max(max, parseFloat(value.name ?? -Infinity));
    }, -Infinity);
  };

  const min = Number(findMinValue(attribute));
  const max = Number(findMaxValue(attribute));
  const selectedIds = filter.attributes.find(item => item.id === attribute.id)?.values ?? [];
  const selectedNumbers = (attribute.values ?? [])
    .filter(value => selectedIds.includes(value.id))
    .map(value => Number(value.name))
    .filter(Number.isFinite);
  const selectedMin = selectedNumbers.length ? Math.min(...selectedNumbers) : min;
  const selectedMax = selectedNumbers.length ? Math.max(...selectedNumbers) : max;

  const [range, setRange] = React.useState<number[]>([selectedMin, selectedMax]);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const userInteractedRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    setRange([selectedMin, selectedMax]);
    // Reset interaction flag so initial/resets don't trigger fetch
    userInteractedRef.current = false;
  }, [clearTrigger, selectedMin, selectedMax]);

  const handleChangeCommit = () => {
    // Blur without an edit must not apply every value from an implicit range.
    if (!userInteractedRef.current) return;
    userInteractedRef.current = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const allValues = (attribute.values ?? []).filter(v => !Number.isNaN(Number(v.name)));
    if (allValues.length === 0) return;

    const inRange = allValues.filter(v => {
      const n = Number(v.name);
      return n >= range[0] && n <= range[1];
    });

    let selected = inRange;

    if (selected.length === 0) {
      const nums = allValues
        .map(v => Number(v.name))
        .filter(n => !Number.isNaN(n))
        .sort((a, b) => a - b);

      const closestTo = (target: number, type: 'min' | 'max') => {
        const candidates =
          type === 'min' ? nums.filter(n => n <= target) : nums.filter(n => n >= target);
        if (candidates.length === 0) return type === 'min' ? nums[0] : nums[nums.length - 1];
        return candidates.reduce((prev, curr) =>
          Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev,
        );
      };

      const snapMin = closestTo(range[0], 'min');
      const snapMax = closestTo(range[1], 'max');
      const low = Math.min(snapMin, snapMax);
      const high = Math.max(snapMin, snapMax);

      // Update visual range to snapped bounds
      setRange([low, high]);

      selected = allValues.filter(v => {
        const n = Number(v.name);
        return n >= low && n <= high;
      });
    }

    // One range edit is one query, never an empty range followed by N partial ranges.
    const nextAttributes = filter.attributes.filter(item => item.id !== attribute.id);
    if (selected.length !== allValues.length) {
      nextAttributes.push({ id: attribute.id, values: selected.map(value => value.id) });
    }
    setFilter({ attributes: nextAttributes });
    selected.forEach(value => {
      updateVariantValues(attribute, value);
    });
  };

  React.useEffect(() => {
    // Skip until the user interacts with slider/inputs
    if (!userInteractedRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleChangeCommit();
    }, 1000);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range[0], range[1]]);

  return (
    <div className={cn('flex flex-col gap-4 py-2', 'ea-attribute-slider', className)}>
      <Slider
        isDual
        value={range}
        min={min}
        max={max}
        aria-label={attribute.name}
        onValueChange={value => {
          userInteractedRef.current = true;
          setRange(value);
        }}
        onBlur={handleChangeCommit}
      />
      <div
        className={cn(
          'flex items-center justify-between gap-2 px-[1px]',
          'ea-attribute-slider__values',
        )}
      >
        <Input
          type="number"
          step={1}
          min={min}
          max={range[1]}
          value={range[0]}
          onChange={e => {
            userInteractedRef.current = true;
            const parsed = parseInt(e.target.value, 10);
            const next = Number.isNaN(parsed) ? min : parsed;
            const clamped = Math.max(min, Math.min(next, range[1]));
            setRange([clamped, range[1]]);
          }}
          onBlur={handleChangeCommit}
          className={{
            wrapper: cn('w-1/2', 'ea-attribute-slider__input-min'),
            main: cn('bg-white'),
          }}
        />
        <Input
          type="number"
          step={1}
          min={range[0]}
          max={max}
          value={range[1]}
          onChange={e => {
            userInteractedRef.current = true;
            const parsed = parseInt(e.target.value, 10);
            const next = Number.isNaN(parsed) ? max : parsed;
            const clamped = Math.min(max, Math.max(next, range[0]));
            setRange([range[0], clamped]);
          }}
          onBlur={handleChangeCommit}
          className={{
            wrapper: cn('w-1/2', 'ea-attribute-slider__input-max'),
            main: cn('bg-white'),
          }}
        />
      </div>
    </div>
  );
}
